// The member-read domain (contract 22 §5 "models/member.types.ts", §6.2 row 10).
//
// ⚠ CONTRACT DRIFT — §5's `AccountMember { id: number }` is UNBUILDABLE. `GET /accounts/{a}/users`
// returns three containers whose ids live in THREE DIFFERENT SPACES, verified against
// `accounts/views.py` on backend `origin/main`:
//
//   • `account_users[].id`                            → a USER id  (`get_dynamic_serialized_data(user_obj)`, views.py:356)
//   • `workspaces[].workspace_permissions[].user_id`  → a USER id  (there is NO `id` key at all, views.py:399-405)
//   • `pending_invites[].id`                          → an INVITE id (`PermissionInvite.pk`, serializer.py:12-14)
//
// The two spaces address different URL families — `accounts/urls.py:18` is
// `users/<user_id>/remove` and `:17` is `permission_invites/<pending_invite_id>/delete`. A single
// `AccountMember.id` therefore routes a revoke to `/users/{inviteId}/remove` on the first numeric
// collision, silently removing the wrong person. So the id spaces are BRANDED and the row is a
// DISCRIMINATED UNION: there is no `.id` to grab, and narrowing is the only way to reach an id.
//
// The read STATE is `SettingsPaneState` (`models/settingsPane.types.ts`) — this module deliberately
// does not mint a twin of it.

/* ── The two id spaces ──────────────────────────────────────────────────────── */

declare const USER_ID_BRAND: unique symbol
declare const INVITE_ID_BRAND: unique symbol

/** A `User` pk. The only id `/accounts/{a}/users/{id}/{remove,update}` accepts. */
export type UserId = number & { readonly [USER_ID_BRAND]: 'UserId' }
/** A `PermissionInvite` pk. The only id `/accounts/{a}/permission_invites/{id}/delete` accepts. */
export type InviteId = number & { readonly [INVITE_ID_BRAND]: 'InviteId' }

/** Brand a raw wire number. The ONLY legal way to mint one — both live in `service/memberList`. */
export const asUserId = (value: number): UserId => value as UserId
export const asInviteId = (value: number): InviteId => value as InviteId

/* ── Roles & scopes ─────────────────────────────────────────────────────────── */

export type MemberRole = 'viewer' | 'viewer_plus' | 'editor' | 'admin' | 'owner'
export type MemberScope = 'account' | 'workspace' | 'dashboard'
/** The legacy's `level.value` (`UsersPermissions.vue:515, 525, 530`), named instead of magic. */
export type MemberScopeLevel = 0 | 1 | 2

export const MEMBER_SCOPE_LEVEL: Readonly<Record<MemberScope, MemberScopeLevel>> = {
  account: 0,
  workspace: 1,
  dashboard: 2,
}

/* ── Wire DTOs — exactly what `ListAccountUsersView` serialises ─────────────── */

/** `views.py:373-377` — only ever present on a NON-owner with an account-scoped access record. */
export interface AccountPermissionDto {
  type?: string
  role_label?: string
  /** `format_role_for_frontend` (helper.py:63) collapses to `'admin' | 'editor' | 'viewer'` ONLY —
   *  it can never emit `viewer_plus`, so an account-scoped Contributor is not expressible. */
  get_permissions?: string
}

/** A `User` row from `get_dynamic_serialized_data` (views.py:356). `id` is the USER pk. */
export interface AccountUserDto {
  id: number
  email?: string
  name?: string | null
  get_account_permissions?: AccountPermissionDto | null
  /** The AI-assistant (MCP) permission — account-wide, one answer per person. Always `true` for
   *  the owner, whose access rides on ownership rather than a permission row. */
  mcp_enabled?: boolean
  [key: string]: unknown
}

/** `views.py:399-405`, verbatim: five keys, `user_id` (no `id`), and `role` as the RAW int. */
export interface WorkspacePermissionDto {
  user_id: number
  /** `UserAccessPermission.Role`: 10 viewer · 15 viewer_plus · 20 editor · 30 admin. The only
   *  lossless role source on this container — `get_permissions` cannot express `viewer_plus`. */
  role?: number | null
  user_email?: string
  /** `string`, or `[]` when `role` is falsy (views.py:403 — the ternary's else branch is a list). */
  get_permissions?: string | string[]
  dashboard_ids?: number[]
  /** Account-wide, NOT this row's own flag — the same answer `account_users[]` carries. */
  mcp_enabled?: boolean
}

export interface WorkspaceWithPermissionsDto {
  id: number | string
  label?: string
  workspace_permissions?: WorkspacePermissionDto[]
  [key: string]: unknown
}

/** `PermissionInviteSerializer` (serializer.py:12-14). NOTE: **no `role` key exists on this model**
 *  (`PermissionInvite`, user_account_configs.py:589-620) — the role is `permission`, a string. */
export interface PendingInviteDto {
  id: number
  email?: string
  /** `'ACCOUNT' | 'WORKSPACE' | 'DASHBOARD'` (uppercase `Permission.<X>.name`, views.py:608-622). */
  permission_type?: string
  /** Workspace ids for a WORKSPACE invite, dashboard ids for a DASHBOARD invite (views.py:719). */
  entity_ids?: number[]
  /** The invited role, verbatim from the invite payload (views.py:726) — CAN be `viewer_plus`. */
  permission?: string
  status?: string
  created_at?: string
  /** Always `[]` in practice: `PermissionInvite.objects.create` never sets it (views.py:721-728).
   *  Dashboard-scoped invites carry their dashboard ids in `entity_ids`. Do not read this. */
  custom_dashboard_ids?: number[]
  /** Copied onto the person's access row when the invite is accepted. */
  mcp_enabled?: boolean
  [key: string]: unknown
}

/** The account's AI-assistant (MCP) seats. `limit: null` = the plan models no ceiling; `0` = the
 *  account has no assistant seats at all. `used` already counts the owner's own seat. */
export interface AssistantSeatsDto {
  limit?: number | null
  used?: number
}

/** The whole payload, after `unwrapEnvelope` peels `{data: …}` (views.py:414). */
export interface AccountUsersDto {
  account_users?: AccountUserDto[]
  pending_invites?: PendingInviteDto[]
  workspaces?: WorkspaceWithPermissionsDto[]
  assistant_seats?: AssistantSeatsDto
}

/** `AssistantSeatsDto`, read defensively — what the invite modal checks a grant against. */
export interface AssistantSeats {
  limit: number | null
  used: number
}

/* ── Domain rows ────────────────────────────────────────────────────────────── */

/** A workspace a grant covers. Structurally satisfied by 07's `WorkspaceItem`. */
export interface MemberWorkspaceRef {
  id: number | string
  label: string
}

/** A dashboard a grant covers. Supplied by the CALLER (09 owns the catalog; CM-22-03/BE-22-06). */
export interface MemberDashboardRef {
  id: number | string
  name: string
  workspace_id: number | string
}

interface MemberRowBase {
  email: string
  name: string | null
  role: MemberRole
  scope: MemberScope
  scopeLevel: MemberScopeLevel
  /** Non-empty only for a workspace-scoped grant. */
  workspaces: MemberWorkspaceRef[]
  /** Non-empty only for a dashboard-scoped grant WITH a resolved catalog (see `buildMemberList`). */
  dashboards: MemberDashboardRef[]
  /** Holds the AI-assistant (MCP) permission. Account-wide, whichever container carried the row. */
  mcpEnabled: boolean
}

/** An accepted member. `userId` is the ONLY id `/users/{id}/…` may receive. */
export interface AccountMemberRow extends MemberRowBase {
  kind: 'member'
  userId: UserId
  isOwner: boolean
}

/** A pending invite. `inviteId` addresses `/permission_invites/{id}/delete` and nothing else. */
export interface PendingInviteRow extends MemberRowBase {
  kind: 'invite'
  inviteId: InviteId
  status: string
  createdAt: string
  /** `>= 24 h` since `created_at` (`format.js:679-687` via `UsersPermissions.vue:522`). */
  isExpired: boolean
}

export type MemberListRow = AccountMemberRow | PendingInviteRow

export const isAccountMemberRow = (row: MemberListRow): row is AccountMemberRow =>
  row.kind === 'member'
export const isPendingInviteRow = (row: MemberListRow): row is PendingInviteRow =>
  row.kind === 'invite'
